import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { SalaryAdvanceCreateDialog } from "@/components/hr/SalaryAdvanceCreateDialog";
import { SalaryPeriodFilterField } from "@/components/hr/SalaryPeriodMonthPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  deleteAdvanceSalaryBatch,
  groupAdvancesByEmployee,
  groupAdvancesByEmployeeMonth,
  listAllSalaryAdvances,
  paymentDestinationLabel,
  periodKey,
  periodLabel,
  salaryAdvancesApiErrorMessage,
  type AdvanceEmployeeMonthGroup,
  type EmployeeSalaryAdvanceLine,
} from "@/lib/hr/salaryAdvancesStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  Eye,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
  Trash2,
  Wallet,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
const ADVANCES_FETCH_CAP = 500;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

type AdvanceSortBy = "updated_desc" | "updated_asc" | "period_desc" | "period_asc";

function employeeLabel(employees: EmployeeRecord[], employeeId: string): string {
  const e = employees.find((x) => x.id === employeeId);
  if (!e) return employeeId || "—";
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeePhoto(employees: EmployeeRecord[], employeeId: string): string | null {
  return employees.find((x) => x.id === employeeId)?.photoDataUrl?.trim() || null;
}

function employeeInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "E";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

function money(amount: string | number | null | undefined): string {
  const n = typeof amount === "string" ? Number(String(amount).replace(/,/g, "")) : Number(amount ?? NaN);
  return formatCurrency(Number.isFinite(n) ? n : null);
}

function formatAdvanceDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseISO(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (!isValid(d)) return iso;
  return format(d, "MMM d, yyyy");
}

function batchPeriodSummary(group: AdvanceEmployeeMonthGroup): string {
  if (group.periodYear != null && group.periodMonth != null) {
    return periodLabel(group.periodYear, group.periodMonth, MONTHS);
  }
  const periods = Array.from(
    new Set(group.lines.map((line) => periodLabel(line.periodYear, line.periodMonth, MONTHS))),
  );
  if (periods.length === 0) return "All periods";
  if (periods.length === 1) return periods[0]!;
  return `${periods.length} periods`;
}

function batchAdvanceDateSummary(group: AdvanceEmployeeMonthGroup): string {
  const dates = group.lines
    .map((line) => formatAdvanceDate(line.advanceDate))
    .filter((v, i, arr) => v !== "—" && arr.indexOf(v) === i);
  return dates.length ? dates.join(" · ") : "—";
}

function batchPaymentSummary(group: AdvanceEmployeeMonthGroup): string {
  const labels = group.lines
    .map((line) =>
      paymentDestinationLabel({
        ...line,
        chequeDate: line.chequeDate ? formatAdvanceDate(line.chequeDate) : null,
      }),
    )
    .filter((v, i, arr) => v && arr.indexOf(v) === i);
  return labels.length ? labels.join(" · ") : "—";
}

function linesByMonth(lines: EmployeeSalaryAdvanceLine[]): Array<{
  key: string;
  periodYear: number;
  periodMonth: number;
  label: string;
  lines: EmployeeSalaryAdvanceLine[];
  totalAmount: number;
}> {
  const map = new Map<string, EmployeeSalaryAdvanceLine[]>();
  for (const line of lines) {
    const key = periodKey(line.periodYear, line.periodMonth);
    const bucket = map.get(key) ?? [];
    bucket.push(line);
    map.set(key, bucket);
  }
  return Array.from(map.entries())
    .map(([key, monthLines]) => {
      const head = monthLines[0]!;
      return {
        key,
        periodYear: head.periodYear,
        periodMonth: head.periodMonth,
        label: periodLabel(head.periodYear, head.periodMonth, MONTHS),
        lines: monthLines,
        totalAmount: monthLines.reduce((sum, row) => sum + (Number(row.advanceAmount.replace(/,/g, "")) || 0), 0),
      };
    })
    .sort((a, b) => b.key.localeCompare(a.key));
}

function AdvanceLinesTable({
  lines,
  totalAmount,
  showPeriodColumns = true,
}: {
  lines: EmployeeSalaryAdvanceLine[];
  totalAmount: number;
  showPeriodColumns?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 shadow-sm">
      <table className="w-full min-w-[40rem] text-sm">
        <thead>
          <tr className="border-b border-border/50 bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="text-left font-semibold py-2.5 px-3">Advance date</th>
            {showPeriodColumns ? (
              <>
                <th className="text-left font-semibold py-2.5 px-3">Period</th>
                <th className="text-left font-semibold py-2.5 px-3">Year</th>
              </>
            ) : null}
            <th className="text-left font-semibold py-2.5 px-3">Payment</th>
            <th className="text-left font-semibold py-2.5 px-3">Reason</th>
            <th className="text-right font-semibold py-2.5 px-3">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b border-border/40 last:border-0">
              <td className="py-2.5 px-3 tabular-nums text-foreground">{formatAdvanceDate(line.advanceDate)}</td>
              {showPeriodColumns ? (
                <>
                  <td className="py-2.5 px-3 text-foreground">
                    {MONTHS.find((m) => m.v === line.periodMonth)?.label ?? line.periodMonth}
                  </td>
                  <td className="py-2.5 px-3 tabular-nums text-muted-foreground">{line.periodYear || "—"}</td>
                </>
              ) : null}
              <td className="py-2.5 px-3 text-muted-foreground">
                <div className="min-w-0">
                  <p className="truncate">{paymentDestinationLabel(line)}</p>
                  {line.bankName ? (
                    <p className="truncate text-[11px] text-muted-foreground/80">{line.bankName}</p>
                  ) : null}
                </div>
              </td>
              <td className="py-2.5 px-3 text-muted-foreground max-w-[14rem]">
                <span className="line-clamp-2" title={line.reason ?? undefined}>
                  {line.reason?.trim() || "—"}
                </span>
              </td>
              <td className="py-2.5 px-3 text-right font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                {money(line.advanceAmount)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-muted/30 font-semibold">
            <td className="py-2.5 px-3" colSpan={showPeriodColumns ? 4 : 2}>
              Total
            </td>
            <td className="py-2.5 px-3" />
            <td className="py-2.5 px-3 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
              {money(totalAmount)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function EmployeeAvatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  return (
    <div className="h-9 w-9 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs font-bold text-primary">{employeeInitial(name)}</span>
      )}
    </div>
  );
}

export default function SalaryAdvances() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "salary_advance_can_create");
  const canDelete = can(user, "salary_advance_can_cancel");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const [lines, setLines] = useState<EmployeeSalaryAdvanceLine[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState<string>("all");
  const [filterPeriodYear, setFilterPeriodYear] = useState<number | null>(() => new Date().getFullYear());
  const [filterPeriodMonth, setFilterPeriodMonth] = useState<number | null>(() => new Date().getMonth() + 1);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [sortBy, setSortBy] = useState<AdvanceSortBy>("updated_desc");
  const [quickDate, setQuickDate] = useState<"today" | "week" | "month" | "all">("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");

  const [modalOpen, setModalOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<AdvanceEmployeeMonthGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdvanceEmployeeMonthGroup | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(PAGE_SIZE_OPTIONS[0]);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listAllSalaryAdvances({ page: 1, perPage: ADVANCES_FETCH_CAP });
      setLines(res.rows);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setLines([]);
        return;
      }
      toast.error(salaryAdvancesApiErrorMessage(e));
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const hasPeriodFilter = filterPeriodYear != null && filterPeriodMonth != null;

  const groups = useMemo(() => {
    const scoped =
      hasPeriodFilter
        ? lines.filter(
            (line) => line.periodYear === filterPeriodYear && line.periodMonth === filterPeriodMonth,
          )
        : lines;
    return hasPeriodFilter ? groupAdvancesByEmployeeMonth(scoped) : groupAdvancesByEmployee(scoped);
  }, [lines, hasPeriodFilter, filterPeriodYear, filterPeriodMonth]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((group) => {
      if (filterEmployee !== "all" && group.employeeId !== filterEmployee) return false;
      if (quickDate !== "all") {
        const updated = parseISO(group.updatedAt);
        if (!isValid(updated)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (updated < start || updated > end) return false;
      }
      if (!q) return true;
      const empName = employeeLabel(employees, group.employeeId).toLowerCase();
      const hay = `${empName} ${group.totalAmount} ${batchPeriodSummary(group)} ${group.lines.map((l) => l.reason ?? "").join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [groups, query, filterEmployee, quickDate, employees]);

  const sortedFiltered = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const aPeriod = periodKey(a.periodYear, a.periodMonth);
      const bPeriod = periodKey(b.periodYear, b.periodMonth);
      switch (sortBy) {
        case "updated_asc":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "period_desc":
          return bPeriod.localeCompare(aPeriod) || b.key.localeCompare(a.key);
        case "period_asc":
          return aPeriod.localeCompare(bPeriod) || a.key.localeCompare(b.key);
        case "updated_desc":
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = (filterEmployee !== "all" ? 1 : 0) + (hasPeriodFilter ? 1 : 0);
  const resetFilters = () => {
    const now = new Date();
    setFilterEmployee("all");
    setFilterPeriodYear(now.getFullYear());
    setFilterPeriodMonth(now.getMonth() + 1);
    setPickerYear(now.getFullYear());
  };

  const paginationKey = `${query}|${filterEmployee}|${filterPeriodYear}|${filterPeriodMonth}|${quickDate}|${sortBy}|${pageSize}`;
  const { paginatedItems, totalCount, totalPages, rangeStart, rangeEnd } = useGridPagination(sortedFiltered, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => setModalOpen(true);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    const target = deleteTarget;
    setDeleting(true);
    try {
      let deleted = 0;
      for (const batchId of target.batchIds) {
        const batchLines = target.lines.filter((line) => (line.batchId || line.id) === batchId);
        if (batchLines.length === 0) continue;
        if (batchLines.some((line) => line.status === "deducted")) continue;
        await deleteAdvanceSalaryBatch({
          employeeId: target.employeeId,
          batchId,
        });
        deleted += 1;
      }
      toast.success(deleted > 0 ? `Deleted ${deleted} advance record${deleted === 1 ? "" : "s"}.` : "Nothing to delete.");
      setDeleteTarget(null);
      if (viewTarget?.key === target.key) setViewTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdvancesApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Advance salary access required"
      message="You don’t have permission to view advance salary. Ask an administrator to grant salary_advance_can_view_any."
    />
  ) : (
    <Layout title="Advance salary">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Advance salary</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Record advance amounts by salary period — each line is deducted when that period&apos;s payroll is processed.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add advance salary
            </Button>
          ) : null}
        </div>

        <SalaryAdvanceCreateDialog
          open={modalOpen}
          onOpenChange={setModalOpen}
          onSaved={() => {
            setPage(1);
            void load();
          }}
        />

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete advance salary?"
          description={
            deleteTarget
              ? `This will remove advance salary for ${employeeLabel(employees, deleteTarget.employeeId)} in ${batchPeriodSummary(deleteTarget)} (${money(deleteTarget.totalAmount)} · ${deleteTarget.lines.length} advance record${deleteTarget.lines.length === 1 ? "" : "s"}).`
              : ""
          }
          confirmLabel={deleting ? "Deleting..." : "Delete"}
          onConfirm={() => void confirmDelete()}
        />

        <Dialog
          open={viewTarget != null}
          onOpenChange={(open) => {
            if (!open) setViewTarget(null);
          }}
        >
          <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-foreground">
                    {viewTarget ? employeeLabel(employees, viewTarget.employeeId) : "Advance details"}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    {viewTarget
                      ? `${batchPeriodSummary(viewTarget)} · ${viewTarget.lines.length} advance record${viewTarget.lines.length === 1 ? "" : "s"} · Total ${money(viewTarget.totalAmount)}`
                      : "Advance salary details"}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="py-1">
              {!viewTarget || viewTarget.lines.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">No advance records for this employee.</div>
              ) : viewTarget.periodYear == null || viewTarget.periodMonth == null ? (
                <div className="space-y-5">
                  {linesByMonth(viewTarget.lines).map((section) => (
                    <div key={section.key} className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground">{section.label}</h3>
                        <span className="text-xs text-muted-foreground">
                          {section.lines.length} advance record{section.lines.length === 1 ? "" : "s"} · {money(section.totalAmount)}
                        </span>
                      </div>
                      <AdvanceLinesTable
                        lines={section.lines}
                        totalAmount={section.totalAmount}
                        showPeriodColumns={false}
                      />
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
                    <span className="text-sm font-semibold text-foreground">Grand total</span>
                    <span className="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                      {money(viewTarget.totalAmount)}
                    </span>
                  </div>
                </div>
              ) : (
                <AdvanceLinesTable
                  lines={viewTarget.lines}
                  totalAmount={viewTarget.totalAmount}
                  showPeriodColumns={false}
                />
              )}
            </div>

            <DialogFooter className="pt-4 border-t border-border/50 gap-2">
              <Button variant="outline" onClick={() => setViewTarget(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="glass-card p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Search</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="glass-input pl-9 h-10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search employee, amount, period…"
                />
              </div>
              <Sheet open={filtersOpen} onOpenChange={(open) => {
                setFiltersOpen(open);
                if (open && filterPeriodYear != null) {
                  setPickerYear(filterPeriodYear);
                }
              }}>
                <SheetTrigger asChild>
                  <Button type="button" variant="glass" className="gap-2 shrink-0">
                    <Filter className="w-4 h-4" />
                    Filters
                    {appliedFiltersCount > 0 ? (
                      <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {appliedFiltersCount}
                      </span>
                    ) : null}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filter advance salary</SheetTitle>
                    <SheetDescription>Refine by employee and salary period.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-6 space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee</Label>
                      <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All employees</SelectItem>
                          {activeEmployees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {employeeLabel(employees, e.id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <SalaryPeriodFilterField
                      selectedYear={filterPeriodYear}
                      selectedMonth={filterPeriodMonth}
                      onChange={(year, month) => {
                        setFilterPeriodYear(year);
                        setFilterPeriodMonth(month);
                      }}
                      browseYear={pickerYear}
                      onBrowseYearChange={setPickerYear}
                    />
                  </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="shrink-0">
              <GridPerPageSelect id="advance-page-size" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            </div>
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AdvanceSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Newest first</SelectItem>
                    <SelectItem value="updated_asc">Oldest first</SelectItem>
                    <SelectItem value="period_desc">Period: Newest first</SelectItem>
                    <SelectItem value="period_asc">Period: Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button type="button" onClick={() => setViewMode("grid")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="Grid view">
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setViewMode("list")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="List view">
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && sortedFiltered.length === 0 ? (
            <div className="text-sm text-muted-foreground">No advance salary records found for these filters.</div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {paginatedItems.map((group, i) => {
                const name = employeeLabel(employees, group.employeeId);
                const photo = employeePhoto(employees, group.employeeId);
                const period = batchPeriodSummary(group);
                return (
                  <motion.div key={group.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                    <CatalogEntityCard
                      title={name}
                      avatarUrl={photo}
                      primaryMeta={{
                        label: "Period",
                        value: `${period} · ${group.lines.length} advance record${group.lines.length === 1 ? "" : "s"} · ${batchAdvanceDateSummary(group)}`,
                        icon: Wallet,
                        iconClassName: "text-emerald-600",
                      }}
                      metaColumns={1}
                      footerRight={money(group.totalAmount)}
                      footerRightClassName="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400"
                      onView={() => setViewTarget(group)}
                      onDelete={canDelete && group.canDelete ? () => setDeleteTarget(group) : undefined}
                    />
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "list" ? (
            <div className="space-y-2">
              {paginatedItems.map((group, i) => {
                const name = employeeLabel(employees, group.employeeId);
                const photo = employeePhoto(employees, group.employeeId);
                const period = batchPeriodSummary(group);
                const metaLine = [
                  period,
                  `${group.lines.length} advance record${group.lines.length === 1 ? "" : "s"}`,
                  batchAdvanceDateSummary(group),
                  batchPaymentSummary(group),
                ]
                  .filter((part) => part && part !== "—")
                  .join(" · ");
                return (
                  <motion.div
                    key={group.key}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.015 }}
                    className="glass-card px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2.5">
                      <EmployeeAvatar name={name} photoUrl={photo} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center justify-between gap-3 min-w-0 flex-1">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-foreground truncate min-w-0">
                                {name}
                              </h3>
                              <p className="mt-0.5 truncate text-xs text-muted-foreground" title={metaLine}>
                                {metaLine}
                              </p>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400 shrink-0 text-right">
                              {money(group.totalAmount)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                                  onClick={() => setViewTarget(group)}
                                  aria-label="View advance details"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">View details</TooltipContent>
                            </Tooltip>
                            {canDelete && group.canDelete ? (
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0 border-destructive/40 text-destructive hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                    onClick={() => setDeleteTarget(group)}
                                    aria-label="Delete advance"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Delete</TooltipContent>
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 ? (
            <GridPaginationFooter page={page} totalPages={totalPages} totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} onPageChange={setPage} />
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
